import clsx from 'clsx';
import { Clock } from 'lucide-react';
import type { ReactNode } from 'react';

import {
  SUGGESTION_COLOR,
  SUGGESTION_ICON,
  type SuggestionBase,
  gainLabel,
} from './suggestion-meta';

export function SuggestionIconChip({
  suggestion,
  size = 'md',
}: {
  suggestion: Pick<SuggestionBase, 'icon' | 'color'>;
  size?: 'md' | 'lg';
}) {
  const Icon = SUGGESTION_ICON[suggestion.icon]?.icon ?? SUGGESTION_ICON.sparkles.icon;
  return (
    <span
      className={clsx(
        'inline-flex shrink-0 items-center justify-center rounded-xl',
        SUGGESTION_COLOR[suggestion.color]?.chip ?? SUGGESTION_COLOR.blue.chip,
        size === 'lg' ? 'h-12 w-12' : 'h-10 w-10',
      )}
    >
      <Icon className={size === 'lg' ? 'h-6 w-6' : 'h-5 w-5'} aria-hidden="true" />
    </span>
  );
}

export function GainPill({ hours }: { hours: number | null }) {
  const label = gainLabel(hours);
  if (!label) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
      <Clock className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  );
}

/**
 * Tuile d'une suggestion (vitrine client et vue Adweb). Toute la tuile ouvre la fiche ;
 * `badges` s'affichent à côté de la catégorie, `footer` en bas de tuile.
 */
export function SuggestionCard({
  suggestion,
  onOpen,
  badges,
  footer,
  muted = false,
}: {
  suggestion: SuggestionBase;
  onOpen: () => void;
  badges?: ReactNode;
  footer?: ReactNode;
  muted?: boolean;
}) {
  return (
    <article
      onClick={onOpen}
      className={clsx(
        'surface group relative flex cursor-pointer flex-col overflow-hidden transition hover:shadow-md',
        muted && 'opacity-70',
      )}
    >
      <div
        className={clsx('h-1 w-full', SUGGESTION_COLOR[suggestion.color]?.bar ?? 'bg-blue-500')}
      />
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start gap-3">
          <SuggestionIconChip suggestion={suggestion} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              {suggestion.category && (
                <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                  {suggestion.category}
                </span>
              )}
              {badges}
            </div>
            {/* Le clic (souris ou clavier) remonte jusqu'à la tuile, qui ouvre la fiche. */}
            <button
              type="button"
              className="mt-0.5 text-left text-base font-semibold text-slate-900 group-hover:text-brand"
            >
              {suggestion.title}
            </button>
          </div>
        </div>
        <p className="line-clamp-3 text-sm text-slate-600">{suggestion.tagline}</p>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-1">
          <GainPill hours={suggestion.hoursSavedPerMonth} />
          {footer}
        </div>
      </div>
    </article>
  );
}
